import {Anchor, Badge, Button, Group, Stack, Table, Text, Tooltip} from '@mantine/core'
import {IconDownload, IconStarFilled} from '@tabler/icons-react'
import {Link} from '@tanstack/react-router'
import type {ReactNode} from 'react'

import {
    activeRentalCounts,
    equipmentLabel,
    formatRentalDay,
    pickupLabel,
    plannedEndLabel,
} from './active-rentals-format'
import {downloadActiveRentalsCsv} from './export-active-rentals-csv'
import type {ActiveRental} from './schemas'

type ActiveRentalsTableProps = {
    rows: ActiveRental[]
    showRep?: boolean
    csvFilename: string
}

function JobLink({
    jobId,
    size,
    dimmed = false,
    children,
}: {
    jobId: string
    size: 'xs' | 'sm'
    dimmed?: boolean
    children: ReactNode
}) {
    return (
        <Anchor
            size={size}
            c={dimmed ? 'dimmed' : undefined}
            renderRoot={(props) => <Link {...props} to="/jobs/$jobId" params={{jobId}} />}
        >
            {children}
        </Anchor>
    )
}

function buildActiveRentalRowKey(row: ActiveRental) {
    return `${row.projectId}:${row.jobId ?? 'order'}`
}

export function ActiveRentalsTable({rows, showRep = true, csvFilename}: ActiveRentalsTableProps) {
    const counts = activeRentalCounts(rows)

    return (
        <Stack gap="sm">
            <Group justify="space-between" wrap="wrap" gap="xs">
                <Text size="sm" c="dimmed" data-testid="active-rentals-summary">
                    {counts.jobs} {counts.jobs === 1 ? 'job' : 'jobs'} with equipment out
                    {counts.pastEndDate > 0 ? ` · ${counts.pastEndDate} past end date` : ''}
                    {counts.noEndDate > 0 ? ` · ${counts.noEndDate} with no end date` : ''}
                    {counts.noPickup > 0 ? ` · ${counts.noPickup} with no pickup` : ''}
                </Text>
                <Button
                    size="xs"
                    variant="default"
                    leftSection={<IconDownload size={14} />}
                    disabled={rows.length === 0}
                    onClick={() => downloadActiveRentalsCsv(rows, csvFilename)}
                >
                    Export CSV
                </Button>
            </Group>
            {rows.length === 0 ? (
                <Text size="sm" c="dimmed">
                    No equipment on rent
                </Text>
            ) : (
                <Table.ScrollContainer minWidth={900}>
                    <Table striped verticalSpacing="xs" fz="sm">
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Customer</Table.Th>
                                <Table.Th>Order / job</Table.Th>
                                {showRep ? <Table.Th>Rep</Table.Th> : null}
                                <Table.Th>Site</Table.Th>
                                <Table.Th>Out</Table.Th>
                                <Table.Th>On rent since</Table.Th>
                                <Table.Th>Planned end</Table.Th>
                                <Table.Th>Pickup</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {rows.map((row) => (
                                <Table.Tr
                                    key={buildActiveRentalRowKey(row)}
                                    data-testid="active-rental-row"
                                >
                                    <Table.Td>{row.customerName}</Table.Td>
                                    <Table.Td>
                                        <Stack gap={0}>
                                            <Anchor
                                                size="sm"
                                                renderRoot={(props) => (
                                                    <Link
                                                        {...props}
                                                        to="/projects/$projectId"
                                                        params={{projectId: row.projectId}}
                                                    />
                                                )}
                                            >
                                                {row.orderNumber
                                                    ? `${row.orderNumber} · ${row.projectName}`
                                                    : row.projectName}
                                            </Anchor>
                                            {row.jobId ? (
                                                <JobLink jobId={row.jobId} size="xs" dimmed>
                                                    {row.jobDisplayId}: {row.jobName}
                                                </JobLink>
                                            ) : null}
                                        </Stack>
                                    </Table.Td>
                                    {showRep ? <Table.Td>{row.repName ?? '—'}</Table.Td> : null}
                                    <Table.Td>{row.sites.join('; ')}</Table.Td>
                                    <Table.Td>
                                        <Group gap={4} wrap="nowrap" align="flex-start">
                                            {row.partialPickup ? (
                                                <Tooltip
                                                    label={`Partial pickup, still on rent: ${row.partialPickup.summary}`}
                                                    multiline
                                                    w={280}
                                                >
                                                    <IconStarFilled
                                                        size={14}
                                                        color="var(--mantine-color-orange-6)"
                                                        aria-label="Partial pickup"
                                                        data-testid="active-rental-partial-pickup"
                                                    />
                                                </Tooltip>
                                            ) : null}
                                            <Stack gap={0}>
                                                {row.equipment.map((line) => (
                                                    <Text size="sm" key={equipmentLabel(line)}>
                                                        {equipmentLabel(line)}
                                                    </Text>
                                                ))}
                                            </Stack>
                                        </Group>
                                    </Table.Td>
                                    <Table.Td>{formatRentalDay(row.onRentSince)}</Table.Td>
                                    <Table.Td>
                                        {row.pastEndDate ? (
                                            <Stack gap={2} align="flex-start">
                                                <Text size="sm">{plannedEndLabel(row)}</Text>
                                                <Badge color="orange" size="sm" variant="light">
                                                    Past end date
                                                </Badge>
                                            </Stack>
                                        ) : row.plannedEndDate === null ? (
                                            <Badge color="yellow" size="sm" variant="light">
                                                No end date
                                            </Badge>
                                        ) : (
                                            plannedEndLabel(row)
                                        )}
                                    </Table.Td>
                                    <Table.Td>
                                        {row.pickup ? (
                                            <JobLink jobId={row.pickup.jobId} size="sm">
                                                {pickupLabel(row)}
                                            </JobLink>
                                        ) : (
                                            <Badge color="red" size="sm" variant="light">
                                                None
                                            </Badge>
                                        )}
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            )}
        </Stack>
    )
}
